import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { Feature, Map, MapBrowserEvent, View } from 'ol';
import { ObjectEvent } from 'ol/Object';
import { ScaleLine, defaults as defaultControls } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import { Extent } from 'ol/extent';
import { WMTSCapabilities } from 'ol/format';
import Point from 'ol/geom/Point';
import { defaults as defaultInteractions } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import { get as getProjection, transform } from 'ol/proj';
import { register } from 'ol/proj/proj4.js';
import VectorSource from 'ol/source/Vector';
import { Style } from 'ol/style';
import proj4 from 'proj4';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_URLS, ApiUrlService } from '../../../../../utils/src/lib/services/api-url.service';
import { IMapConfig } from '../../models/config-mappa.model';
import { AssetsService } from '../../services/assets.service';
import { BookmarkService } from '../../services/bookmark.service';
import { ConfigSidebarService } from '../../services/config-sidebar.service';
import { DrawingService } from '../../services/drawing.service';
import { FeatureSelectService } from '../../services/feature-select.service';
import { LayerStylesService } from '../../services/layer-styles.service';
import { LayerPosition, MapService } from '../../services/map.service';
import { PlotService } from '../../services/plot.service';
import { PsaService } from '../../services/psa.service';
import { RischioAnaliticoStateService } from '../../services/rischio-analitico-state.service';

/**
 * Wrapper for OpenLayers and Google Maps maps components
 */
@Component({
  selector: 'ebw-map',
  templateUrl: './ebw-map.component.html',
  styleUrls: ['./ebw-map.component.scss']
})
export class EbwMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() mapConfiguration: IMapConfig;

  mapCenter3857: number[] = null;
  mapCenter4326: number[] = null;
  googleMapsCenter: { lat: number; lng: number } = null;
  mapZoom: number = null;
  mapResolution: number = null;
  mapExtent: Extent = null;

  activeMap: number;
  /** Whether the Google Maps basemap div should be shown (false hides it past a world layer's maxZoom, letting the OL WMTS overlay show through). */
  googleBasemapVisible = true;

  apiLoaded: Observable<boolean>;
  googleMapsScript = null;
  @ViewChildren(GoogleMap) googleMapsComponentViewer: QueryList<GoogleMap>; //For some reason it has problems with the API loading and the ViewChild;
  googleMapsMap: GoogleMap = null;
  openLayersMap: Map;

  subscriptions: Subscription[] = [];

  googleMapsOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    streetViewControl: false,
    keyboardShortcuts: false,
    clickableIcons: false,
    disableDoubleClickZoom: true,
    gestureHandling: 'none',
    scrollwheel: false,
    panControl: false,
    rotateControl: false,
    fullscreenControl: false,
    isFractionalZoomEnabled: false,
    tilt: 0
  };

  constructor(
    private httpClient: HttpClient,
    public mapService: MapService,
    private featureSelectService: FeatureSelectService,
    private drawingService: DrawingService,
    private _plotService: PlotService,
    private _psaService: PsaService,
    private assetsService: AssetsService,
    private styleService: LayerStylesService,
    private configSidebarService: ConfigSidebarService,
    private utilsService: UtilsService,
    private changeDetectorRef: ChangeDetectorRef,
    private _ebwModalService: EbwModalService,
    private _bookmarkService: BookmarkService,
    private _i18nService: I18NService,
    private _apiUrlService: ApiUrlService,
    private _state: RischioAnaliticoStateService
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    const mapConfChanges = changes.mapConfiguration;
    if (mapConfChanges && !mapConfChanges.firstChange) {
      // se la configurazione cambia aggiorno la lista dei livelli
      // possibile futura implementazione aggiornare interamente la mappa al cambio della configurazione
      this.setMapLayer();
    }
  }

  initSubscriptions() {
    this.subscriptions.push(
      this.mapService.mapZoomChanged.subscribe((newZoom) => {
        if (this.mapService.readyGoogleMapsMap && this.mapService.readyOpenLayersMap) {
          // this.openLayersMap.getView().setZoom(newZoom);
          this.googleMapsMap.googleMap.setZoom(newZoom);
        }
      })
    );

    this.subscriptions.push(
      this.mapService.showingMapChanged.subscribe((showing) => {
        this.activeMap = showing;
      })
    );

    this.subscriptions.push(
      this.mapService.googleBasemapVisible$.subscribe((visible) => {
        this.googleBasemapVisible = visible;
      })
    );
  }

  ngAfterViewInit(): void {
    this.googleMapsComponentViewer.changes.subscribe((comps: QueryList<GoogleMap>) => {
      if (this.mapConfiguration.google_key) {
        //if it is the first time and the map is null
        if (!this.googleMapsMap) {
          this.googleMapsMap = comps.first;
          this.initGoogleMapsMap();
        }
      }
    });

    // this.googleMapsMap.triggerResize();

    //DO NOT REMOVE THIS - IT IS TO MANUALLY SAY ANGULAR TO GET CHANGES FROM THE GOOGLE MAPS COMPONENT
    //  AFTER IT IS BEING LOADED
    this.googleMapsComponentViewer.notifyOnChanges();
    //Initialize maps
    this.initOpenLayersMap();

    //Init value subscriptions after maps are initialized
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.mapService.readyGoogleMapsMap = false;
    this.mapService.readyOpenLayersMap = false;
    this.mapService.openLayersMap = null;
    this.mapService.googleMapsMap = null;
    this.googleMapsMap = null;
    this.openLayersMap = null;

    this.mapService.resetService();
    this.featureSelectService.featuresSelected = [];
    this.featureSelectService.featuresOpen = false;

    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  ngOnInit(): void {
    if (this.mapConfiguration.google_key) {
      //Check if the API is already loaded
      let scriptsList = Array.from(document.getElementsByTagName('script'));
      scriptsList = scriptsList.filter((element) => {
        return element.src.includes('maps.googleapis.com');
      });

      if (scriptsList.length == 0) {
        //If the Google Maps API has not been added, call it.
        let googleApisUrl = `https://maps.googleapis.com/maps/api/js?key=${this.mapConfiguration.google_key}&libraries=places`;

        this.apiLoaded = this.httpClient.jsonp(googleApisUrl, 'callback').pipe(
          map(() => {
            return true;
          }),
          catchError(() => of(false))
        );
      } else {
        //If the API is already loaded, do not do the call
        this.apiLoaded = of(true);
      }
    } else {
      this.apiLoaded = of(false);
    }

    this.activeMap = this.activeMap;

    //Initialize stored values from mapService or put default ones
    //Initialize map center
    if (this.mapService.mapCenter) {
      this.mapCenter3857 = transform(this.mapService.mapCenter, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
      this.googleMapsCenter = {
        lat: this.mapService.mapCenter[0],
        lng: this.mapService.mapCenter[1]
      };
      this.mapCenter4326 = this.mapService.mapCenter;
    } else {
      const center4326 = [this.mapConfiguration.mapSetup.coordinate[0], this.mapConfiguration.mapSetup.coordinate[1]];
      this.mapCenter3857 = transform(center4326, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
      this.mapCenter4326 = center4326;
      this.googleMapsCenter = this.getGoogleMapsCenter(center4326);
    }

    //Initialize map zoom
    this.mapZoom = this.mapService.mapZoom ? this.mapService.mapZoom : this.mapConfiguration.mapSetup.zoom;

    //Get the list of base layers (world layers) from the config file.
    let basemapLayers = this.getWorldLayers();

    //Get the base layer to be displayed and the corresponding map
    for (let baseLayer of basemapLayers) {
      if (baseLayer.get(MapService.VISIBILITY_LAYER_KEY)) {
        //Set the global base layer on the mapService
        this.mapService.activeBaseLayer = baseLayer;
        if (baseLayer.get(MapService.TYPE_GOOGLE_LAYER_KEY) == '') {
          this.activeMap = this.mapService.SHOW_OPEN_LAYERS;
        } else {
          this.mapService.googleMapsBasemapType = baseLayer.get(MapService.TYPE_GOOGLE_LAYER_KEY);
          this.activeMap = this.mapService.SHOW_GOOGLE_MAPS;
        }
      }
    }
  }

  initGoogleMapsMap() {
    this.mapService.mapCenter = this.mapCenter4326;
    this.mapService.googleMapsMap = this.googleMapsMap;
    this.mapService.readyGoogleMapsMap = true;
    this.googleMapsMap.googleMap.setMapTypeId(this.mapService.googleMapsBasemapType);

    this.changeDetectorRef.detectChanges();
  }

  initOpenLayersMap() {
    this.mapService.mapCenter = this.mapCenter4326;
    let basemaps = [];
    this.mapService.worldLayers.forEach((value, key) => {
      basemaps.push(value);
    });

    //Initialize the styles from the configuration
    const configuration = this.mapConfiguration;

    for (let projection of configuration.mapProjections) {
      if (projection.code != 'EPSG:3857' && projection.code != 'EPSG:4326') {
        proj4.defs(projection.code, projection.proj);
        register(proj4);

        if (projection.extent) {
          getProjection(projection.code).setExtent(projection.extent);
        }
      }
    }

    if (configuration && configuration.styles) {
      this.styleService.mapStyles = configuration.styles;
    } else {
      this.styleService.mapStyles = [];
    }

    //Open Layers Map initialization
    this.openLayersMap = new Map({
      view: new View({
        constrainResolution: true,
        center: this.mapCenter3857,
        zoom: this.mapConfiguration.mapSetup.zoom,
        maxZoom: this.mapConfiguration.mapSetup.zoomMax
      }),
      //The basemaps that were recently listed and loaded.
      layers: basemaps,
      controls: defaultControls({
        zoom: false
      }).extend([
        new ScaleLine({
          units: 'metric',
          bar: true,
          text: true,
          minWidth: 140,
          maxWidth: 250,
          target: 'scaleline'
        })
      ]),
      // }).getArray(),
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false,
        shiftDragZoom: false,
        dragPan: true,
        zoomDuration: 0
      }).getArray(),
      target: 'ol-map'
    });

    this._bookmarkService
      .getFavouriteBookmark(
        this._apiUrlService.createUrl(API_URLS.MAP.BOOKMARK.GET_FAVOURITE).concat(`?user_id=${sessionStorage.getItem(LoginService.USER_ID_KEY)}`)
      )
      .then((result) => {
        if (result) {
          if (!!this.mapConfiguration.google_key) {
            this.mapService.readyGoogleMapsMapChanged.subscribe((isReady) => {
              if (isReady && result) {
                this._bookmarkService.positioningOnMap(result.bbox, result.active_layers);
              }
            });
          } else {
            this._bookmarkService.positioningOnMap(result.bbox, result.active_layers);
          }
        }
      })
      .catch((error) => {
        this._ebwModalService.openErrorModal({
          title: this._i18nService.getInstantValue('common.error'),
          content: error?.error?.message ?? 'Errore'
        });
      });

    // end here

    //Get initial map values for the MapService
    this.mapZoom = this.openLayersMap.getView().getZoom();
    this.mapService.mapZoom = this.mapZoom;
    this.mapService.maxZoom = this.mapConfiguration.mapSetup.zoomMax;
    this.mapService.minZoom = this.mapConfiguration.mapSetup.zoomMin;
    if (this.mapConfiguration.layerOrder) {
      this.mapService.layerOrder = this.mapConfiguration.layerOrder;
    }
    this.mapResolution = this.openLayersMap.getView().getResolution();
    this.mapService.mapResolution = this.mapResolution;
    this.mapExtent = this.openLayersMap.getView().calculateExtent();
    this.mapService.mapExtent = this.mapExtent;
    this.mapService.registeredProjections = configuration.mapProjections;

    //Append the listeners for center and resolution changes
    this.openLayersMap.getView().on('change:center', (event: ObjectEvent) => {
      this.mapCenterChangedEvent(event);
    });

    this.openLayersMap.getView().on('change:resolution', (event: ObjectEvent) => {
      this.resolutionChangedEvent(event);
    });

    //Append the pointermove event
    this.openLayersMap.on('pointermove', (event) => {
      this.mouseMoveEvent(event);
    });

    this.openLayersMap.on('singleclick', (event) => {
      this.mapClickEventHandler(event);
    });

    // this.openLayersMap.on(
    //   "precompose",
    //   (event) => {
    //     event.context.fillStyle = 'white';
    //     event.context.fillRect(0, 0, event.context.canvas.width, event.context.canvas.height);
    //   }
    // )

    //rendercomplete
    this.openLayersMap.once('postrender', (event) => {
      this.mapService.readyOpenLayersMap = true;
    });

    this.openLayersMap.on('moveend', (event) => {
      // Avendo rimosso la modalità sidebar (mai implementata interamente), questo evento non serve
    });

    //Define the map on the service after everything is loaded so it can be used globally
    this.mapService.openLayersMap = this.openLayersMap;

    // The initial active basemap was set directly above (not via changeWorldLayer), so the
    // zoom-based Google basemap visibility watch was never installed for it — do it now that
    // the OL view exists.
    if (this.mapService.activeBaseLayer) {
      this.mapService.activateWorldLayerZoomWatch(this.mapService.activeBaseLayer);
    }

    this.setMapLayer();
  }

  public async setMapLayer() {
    await this.addOverlayLayers();

    this.addAssetsLayer();

    this.addTemporaryLayer2();
    this.addTemporaryLayer();
    // notify that all the layers have been loaded
    this.mapService.mapLayersLoaded$.next(true);
  }

  /**
   * Handler per l'evento di modifica del centro mappa
   * @param   event evento lanciato da Angular
   */
  mapCenterChangedEvent(event?: ObjectEvent) {
    this.mapCenter3857 = this.openLayersMap.getView().getCenter();
    this.mapCenter4326 = transform(this.mapCenter3857, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    this.googleMapsCenter = this.getGoogleMapsCenter(this.mapCenter4326);
    this.mapExtent = this.openLayersMap.getView().calculateExtent();
    this.mapService.mapExtent = this.mapExtent;
    this.mapService.googleMapsMap && this.mapService.googleMapsMap.googleMap.setCenter(this.googleMapsCenter);
    this.mapService.mapCenter = this.mapCenter4326;

    this.changeDetectorRef.detectChanges();
  }

  resolutionChangedEvent(event: ObjectEvent) {
    this.mapZoom = this.openLayersMap.getView().getZoom();
    this.mapService.mapZoom = this.mapZoom;
    this.mapResolution = this.openLayersMap.getView().getResolution();
    this.mapService.mapResolution = this.mapResolution;
    this.mapExtent = this.openLayersMap.getView().calculateExtent();
    this.mapService.mapExtent = this.mapExtent;
  }

  mouseMoveEvent(event) {
    //Must transform the coordinates to projection 4326 to have (latitude,longitude) instead of (X,Y)
    let mouseCoordinates = transform(event.coordinate, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    this.mapService.mouseCoordinates = mouseCoordinates;
  }

  mapClickEventHandler(event: MapBrowserEvent<any>) {
    // Se è un click gestito in modo custom
    if (this.mapService.customMapClickEnable$.value && !this.drawingService.drawingToolOpen) {
      this.mapService.mapClicked$.next(event);
      return;
    }
    // Se il tool di plotting o di disegno o il PSA sono aperti non faccio niente, è gestito tutto altrove
    if (
      this._plotService.isPlotOpen() ||
      this._psaService.isVisibleDuplicatePsa ||
      this.drawingService.drawingToolOpen ||
      this._state.analyticRiskIsOpen$.value
    ) {
      return;
    }
    const searchPoint = new Point(event.coordinate);
    // Cerco gli asset nei layer attivi
    this.featureSelectService.searchFeaturesInActiveLayers(searchPoint);
  }

  /**
   * Transform an OL coordinate to a google maps center object with latitude and longitude
   * @param mapCenter OL coordinate to transform.
   * @returns A lat,lng object that can be used as center for google maps.
   */
  getGoogleMapsCenter(mapCenter: Coordinate): { lat: number; lng: number } {
    return {
      lat: mapCenter[1],
      lng: mapCenter[0]
    };
  }

  getWorldLayers(): BaseLayer[] {
    let worldLayers = [];

    this.mapConfiguration.worldLayers
      .filter((layer) => layer.position === LayerPosition.BASE)
      .forEach((layer) => {
        worldLayers.push(this.mapService.createWorldLayer(layer));
      });

    return worldLayers;
  }

  addAssetsLayer() {
    let assetsLayer = new VectorLayer({
      source: new VectorSource({}),
      style: (feature: Feature) => {
        const styleId = feature.get('style');
        if (styleId) {
          const newStyle = this.styleService.buildFeatureStyleFromId(styleId);
          return newStyle;
        } else {
          return new Style();
        }
      }
    });

    this.assetsService.assetsLayer = assetsLayer;
    this.openLayersMap.addLayer(assetsLayer);

    this.assetsService.requestAssets();
  }

  addTemporaryLayer() {
    let tempLayer = new VectorLayer({
      source: new VectorSource({}),
      style: (feature: Feature) => {
        const styleId = feature.get('style');
        if (styleId) {
          const newStyle = this.styleService.buildFeatureStyleFromId(styleId);
          return newStyle;
        } else {
          return new Style();
        }
      }
    });

    this.mapService.tempLayer = tempLayer;
    this.openLayersMap.addLayer(tempLayer);
  }

  addTemporaryLayer2() {
    let tempLayer2 = new VectorLayer({
      source: new VectorSource({}),
      style: (feature: Feature) => {
        const styleId = feature.get('style');
        if (styleId) {
          const newStyle = this.styleService.buildFeatureStyleFromId(styleId);
          return newStyle;
        } else {
          return new Style();
        }
      }
    });

    this.mapService.tempLayer2 = tempLayer2;
    this.openLayersMap.addLayer(tempLayer2);
  }

  async addOverlayLayers() {
    let overlayLayers = this.mapService.getCustomLayers('SHAPE').concat(this.mapService.getCustomLayers('THIRDPARTY'));

    for (let layer of overlayLayers) {
      this.mapService.removeLayer(layer.id);
    }

    this.mapService.proxyUrl = this.mapConfiguration.proxyURL ? this.mapConfiguration.proxyURL + '?url=' : '';

    this.mapConfiguration.wmsLayers
      .filter((layer) => layer.position === LayerPosition.OVERLAY)
      .forEach((layer) => {
        if (layer.ui_params.image) {
          let newLayer = this.mapService.addImageWMSLayer(layer);
        } else {
          let newLayer = this.mapService.addWMSLayer(layer, true);
        }
      });

    // obtain all the overlay WMTS layers
    let overlayWMTSLayers = this.mapConfiguration.wmtsLayers.filter((layer) => layer.position === LayerPosition.OVERLAY);

    // list of URLs to obtain capabilities from
    let capabilitiesUrls = [];

    // prepare the capabilities parser and the headers for the HTTP request
    const wmtsParser = new WMTSCapabilities();
    const headers = new HttpHeaders({ Accept: 'text/xml' });

    // cycle through every layer
    for (let layer of overlayWMTSLayers) {
      // if it's a third party layer
      if (layer.layer_group == 'THIRDPARTY') {
        // check if the capability URL has already been stored
        let currentCapability = capabilitiesUrls.find((element) => {
          return element === layer.url;
        });
        // if it hasn't been stored yet, store it in the list
        if (!currentCapability) {
          capabilitiesUrls.push(layer.url);
        }
      }
      // if it's a normal layer, load it into the map without its respective capabilities
      else if (layer.layer_group == 'MAPSERVICE') {
        if (await this.configSidebarService.checkLayerVisibility(layer.id)) {
          let newLayer = this.mapService.addWMTSLayer(layer, this.mapConfiguration.mapSetup.zoomMax);
        }
      } else {
        let newLayer = this.mapService.addWMTSLayer(layer, this.mapConfiguration.mapSetup.zoomMax);
      }
    }

    // list of capability requests
    let capabilityRequests = [];

    // cycle through all the URLs for capabilities
    for (let capabilityUrl of capabilitiesUrls) {
      // make the requests to get the capabilities and create the relative layers in the map
      let url = this.mapService.proxyUrl + capabilityUrl + '&SERVICE=WMTS&REQUEST=GetCapabilities';

      let request = this.utilsService
        .getRequest(url, {}, { headers: headers, responseType: 'text' })
        .toPromise()
        .then((response) => {
          const capabilities = wmtsParser.read(response);

          for (let layer of overlayWMTSLayers) {
            if (layer.layer_group == 'THIRDPARTY' && layer.url == capabilityUrl) {
              let newLayer = this.mapService.addWMTSLayer(layer, this.mapConfiguration.mapSetup.zoomMax, false, capabilities);
            }
          }
        });

      // store the requests in the list
      capabilityRequests.push(request);
    }

    // await for all the capability requests to finish before proceeding
    await Promise.all(capabilityRequests);

    this.mapConfiguration.xyzLayers
      .filter((layer) => layer.position === LayerPosition.OVERLAY)
      .forEach((layer) => this.mapService.addXYZLayer(layer));

    this.mapConfiguration.vectorLayers
      .filter((layer) => layer.position === LayerPosition.OVERLAY)
      .forEach((layer) => this.mapService.addVectorLayer(layer));
  }
}
